import { useEffect, useCallback, useRef, type ReactNode } from "react"
import { iframeMessenger } from "@/services/iframeMessenger"
import { type PendoGlobal } from "@/hooks/usePendo"
import { PendoContext, type PendoContextValue } from "./pendoContext"

export interface PendoProviderProps {
  children: ReactNode;
}

export function PendoProvider({ children }: PendoProviderProps) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const pendoGlobal = (window as any).pendo as PendoGlobal | undefined

  // Use a ref to capture pendoGlobal once and avoid adding it as a dependency,
  // since it's window.pendo which is set on page load and never changes.
  const pendoRef = useRef(pendoGlobal)

  useEffect(() => {
    if (!pendoRef.current) {
      console.warn(
        "Pendo global object not found. Telemetry will not be tracked.",
      )
    }
  }, [])

  useEffect(() => {
    // Wire up parent-frame visitor identity messages once, with cleanup.
    if (!pendoRef.current) return

    const unsubVisitor = iframeMessenger.on("SET_VISITOR", (message) => {
      pendoRef.current?.initialize?.(message.payload)
    })

    const unsubClear = iframeMessenger.on("CLEAR_VISITOR", () => {
      pendoRef.current?.clearSession?.()
    })

    return () => {
      unsubVisitor()
      unsubClear()
    }
  }, []) // Safe to use empty array since pendoRef.current never changes

  const track = useCallback<PendoContextValue["track"]>(
    (eventName, properties) => {
      pendoRef.current?.track?.(eventName, properties)
    },
    [],
  )

  const CONTENT_MAX_LENGTH = 500

  const trackAgent = useCallback<PendoContextValue["trackAgent"]>(
    (eventType, metadata) => {
      try {
        pendoRef.current?.trackAgent?.(eventType, {
          ...metadata,
          content:
            metadata.content.length > CONTENT_MAX_LENGTH
              ? metadata.content.slice(0, CONTENT_MAX_LENGTH)
              : metadata.content,
        })
      } catch {
        // Analytics must never break the product
      }
    },
    [],
  )

  return (
    <PendoContext.Provider value={{ track, trackAgent }}>
      {children}
    </PendoContext.Provider>
  )
}
